import * as d3 from "d3";
import { format } from "date-fns";
import { round } from "lodash";
import { enCA, fr } from "date-fns/locale";
import i18n from "i18next";

export const _60 = 60;
export const ONE_SECOND_OF_MS = 1000;
export const ONE_MINUTE_OF_MS = _60 * ONE_SECOND_OF_MS;
export const ONE_HOUR_OF_MS = _60 * ONE_MINUTE_OF_MS;
export const ONE_DAY_OF_MS = 24 * ONE_HOUR_OF_MS;
export const ONE_WEEK_OF_MS = 7 * ONE_DAY_OF_MS;
export const NUM_DAYS_IN_WEEK = 7;
export const NUM_DAYS_IN_YEAR = 365;
export const WEEKS_IN_YEAR = 52;

export const EASTERN_STANDARD_TIMEZONE = "America/New_York"; // used to convert Date objct to eastern standard time (hours modifier)

// Date and time format strings for use with date-fns
// https://date-fns.org/v2.27.0/docs/format
export const FORMAT_READABLE_WEEK_MONTH_TIME = "ddd MMM d HH:mm";
export const FORMAT_READABLE_DATE_WITH_DAY = "dddd MMM D, YYYY";
export const FORMAT_READABLE_DATE_WITH_SHORT_DAY = "ddd MMM d, yyyy";
export const FORMAT_READABLE_DATE = "MMM d yyyy";
export const FORMAT_TIME_12 = "h:mma";
export const FORMAT_TIME_24 = "HH:mm";
export const FORMAT_TIME = "HH:mm";
export const FORMAT_DAY_TIME = "MMMM d K:mm a";
export const FORMAT_DATE_HOUR_FR = "d MMMM k'h'";
export const FORMAT_TIME_WITH_SECONDS = "HH:mm:ss";
export const FORMAT_HOURS = "HH";
export const FORMAT_DATE_NUMBERS = "MM/d/yyyy";
export const FORMAT_DATE_NUMBERS_NO_YEAR = "MM/d";
export const FORMAT_TIME_HOURS_ONLY = "H";
export const FORMAT_MONTH_YEAR = "M-yyyy";
export const FORMAT_MONTH_DAY = "MMM-d";
export const FORMAT_MONTH_LONG = "MMMM";
export const FORMAT_MONTH = "MMM";
export const LONG_FORM_MONTH_DAY = "MMMM do";

// Locale for use with D3
export const FORMAT_D3_DATE = "%b-%d";
export const FORMAT_D3_TIME = "%H:%M";
export const FORMAT_D3_DATE_TIME = "%b-%d %H:%M";
export const FORMAT_D3_TIME_WITH_SECONDS = "%H:%M:%S";
export const locale = d3.timeFormatLocale({
  dateTime: "%a %b %e %X %Y",
  date: "%Y-%m-%d",
  time: "%H:%M:%S",
  periods: ["am", "pm"],
  days: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  shortMonths: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
});

export const GREENWICH_TIMEZONE = "Etc/GMT";

const truncateTime = (startTimeMs: number, currentTimeMs: number) => {
  let displayedTime;
  let displayedUnit = "min.";

  const duration = currentTimeMs - startTimeMs;

  // return null-like if > 999 days (data not ready)
  const MAX_DURATION_MS = ONE_DAY_OF_MS * 999;
  if (duration > MAX_DURATION_MS) {
    return { displayedTime: "--", displayedUnit: "" };
  }

  // Floor the time, but don't display negative durations. Negative
  // durations can happen when clocks are not in sync.
  displayedTime = Math.max(0, Math.floor(duration / ONE_MINUTE_OF_MS));

  if (displayedTime > 999) {
    displayedTime = Math.floor(duration / ONE_HOUR_OF_MS);
    displayedUnit = displayedTime < 2 ? "hr." : "hrs.";
    if (displayedTime > 999) {
      displayedTime = Math.floor(duration / ONE_DAY_OF_MS);
      displayedUnit = displayedTime < 2 ? "day" : "days";
    }
  }

  return { displayedTime, displayedUnit };
};

export const convertSecondsToFormattedMinutes = targetRate => {
  const minutes = Math.floor(targetRate / _60);
  const seconds = targetRate - _60 * minutes;
  return minutes > 0 ? `${minutes}m${seconds}s` : `${seconds}s`;
};

export default truncateTime;

export const getFormattedTimes = ({
  time,
  isTest,
}: {
  time: number;
  isTest: boolean;
}) => {
  const hoursMinutes = isTest ? "test" : format(time, FORMAT_TIME);
  const hour = isTest ? "test" : format(time, FORMAT_HOURS);
  const minute = isTest ? "test" : format(time, "mm");
  const day = isTest ? "test" : new Date(time).getDate();
  const month = isTest ? "test" : format(time, "MMM");
  const shortWeekday = isTest ? "test" : format(time, "ddd");
  return {
    hoursMinutes,
    hour,
    minute,
    day,
    month,
    shortWeekday,
  };
};

export const getFormattedHoursMinutes = timeMs => {
  const msToHours = timeMs / ONE_HOUR_OF_MS;
  const hours =
    timeMs > 0
      ? Math.floor(msToHours).toFixed(0)
      : Math.ceil(msToHours).toFixed(0);
  const minutes = round((timeMs % ONE_HOUR_OF_MS) / ONE_MINUTE_OF_MS, 0);

  return minutes === _60
    ? { hours: Number(hours) + 1, minutes: 0 }
    : { hours, minutes };
};

// This gives the same results as rounding decimal time (i.e. 1.5 rounds up to 2, 1.49 rounds down to 1).
export const getRoundedMinutesMs = (timeMs: number): number => {
  const remainder = timeMs % ONE_MINUTE_OF_MS;
  let adjustedTimeMs;
  if (ONE_MINUTE_OF_MS <= 2 * remainder) {
    // round up
    adjustedTimeMs = timeMs + ONE_MINUTE_OF_MS - remainder;
  } else {
    // round down
    adjustedTimeMs = timeMs - remainder;
  }
  return adjustedTimeMs;
};

export const getReadableHoursAndMinutes = (timeMs: number) => {
  const { hours, minutes } = getFormattedHoursMinutes(timeMs);
  return `${Number(hours) !== 0 ? hours + "h" : ""} ${minutes}m`;
};

export const getFormattedMinutes = timeMs => {
  return round(timeMs / ONE_MINUTE_OF_MS, 0);
};

export const getMidnightTomorrowMs = now =>
  new Date(now + ONE_DAY_OF_MS).setHours(0, 0, 0, 0);

export const addOneHourToStringTime = timeString => {
  return `${parseInt(timeString, 10) + ONE_HOUR_OF_MS}`;
};

export const getFullDayRangeTitle = (startTimeMs, endTimeMs) =>
  `${format(startTimeMs, FORMAT_DAY_TIME)}-${format(
    endTimeMs,
    FORMAT_DAY_TIME
  )}`;

export const loop = ({ cb, interval, ref }) => {
  return window.setTimeout(() => {
    cb();
    ref.current = loop({ cb, interval, ref });
  }, interval);
};

/**
 * this function will get the last full week starting from the given day of the week.
 * @param dayOfWeek Day of the week (e.g. monday = 1, tuesday =2)
 */
export const getLastFullWeekFromDayOfWeek = (dayOfWeek: number) => {
  const now = new Date();
  now.setHours(0);
  now.setMinutes(0);
  now.setSeconds(0);
  now.setMilliseconds(0); // start with a neutrally set start date

  const currentLastWeek = getLastFullWeek(now); // we get the last 7 days and use it to determine the real day we need to start our week from
  const requiredDayOfWeek = currentLastWeek.filter(({ ge, lt }) => {
    return lt.getDay() === dayOfWeek;
  }); // we will then use this acquired date to get our real week

  return getLastFullWeek(requiredDayOfWeek[0].ge).reverse(); // reverse so our time is in order
};

export const getLastFullWeek = (endDate: Date) => {
  return [...Array(7)].map((entry, index) => {
    const ge = new Date(endDate.getTime() - (index + 1) * ONE_DAY_OF_MS);
    const lt = new Date(endDate.getTime() - index * ONE_DAY_OF_MS);
    return { ge, lt };
  });
};

/**
 *
 * this function will get the last full quarter and its year from the given date (usually the current date)
 * @returns qString that indicates which Quarter Data we wand and year of wanted data
 */
export const getLastFullQuarter = () => {
  const todaysDate = new Date(); // get current date
  const currentMonth = todaysDate.getMonth() + 1; // months start from 0, we need to compensate accordingly for edge cases

  const quarter = String(
    Math.ceil(currentMonth / 3) !== 1 ? Math.ceil(currentMonth / 3) - 1 : 4 // -1 in equation to represent previous quarter
  ); // determine last full quarter number (1-4)
  const year =
    quarter === "4" ? todaysDate.getFullYear() - 1 : todaysDate.getFullYear(); // determine the year of said quarter
  const qString = `Q${quarter}`; // configure a Q{number 1-4} string used for getting data
  return { qString, year };
};

/**
 * Get translated readable time from epoch time
 *
 * @param {{
 *   epochTime: number;
 *   formatString: string;
 * }} {
 *   epochTime,
 *   formatString,
 * }
 * @return {*}  {string}
 */
export const getEpochToReadableTime = ({
  epochTime,
  formatString,
}: {
  epochTime: number;
  formatString: string;
}): string => {
  const locale = { locale: i18n.language === "fr" ? fr : enCA };
  return format(epochTime, formatString, { ...locale });
};

/**
 * Get date of monday of the current week
 *
 * @param {*} date
 * @returns {Date} Monday of last completed week
 */
export const getMonday = (date: number): Date => {
  const dateObj = new Date(date);
  const day = dateObj.getDay(),
    diff = dateObj.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  return new Date(dateObj.setDate(diff));
};
